import { t as tr } from '@hana/i18n';
import './OpsTripDrawer.i18n';
import { IconAlertTriangle, IconCamera, IconClock, IconExternalLink, IconHistory, IconReceipt, IconRoute, IconSparkles, IconTicket, IconCoin } from '@tabler/icons-react';
import { Button, Drawer, useToast } from '@hana/ui';
import { CARGO_LABEL, FEE_NAME, NATURE_LABEL, PAYER_LABEL, PERSON, STATUS_LABEL, dayLabel, fullVnd, isOver, pct, th, MASK, type OpsLine, type OpsTrip } from './opscostData';
import s from './opscostDrawer.module.css';

// Mint-OpsCost · DRAWER "Chi tiết chi phí chuyến" (640): chip tóm tắt · kiểm bất thường · lộ trình · các khoản chi · VETC đối chiếu · phụ thu bán ra · lịch sử.
// Chỉ đọc; nút ghi báo toast. Màn tiền → số che khi thiếu quyền (`mask`).

const DOC_LABEL: Readonly<Record<string, string>> = { INV: 'Hoá đơn', PND: 'Chờ hoá đơn', RCP: 'Biên lai / bảng kê', NDC: 'Không chứng từ' };
const MATCH = {
  ok: ['Khớp sao kê', 'ok', 'Khớp giao dịch trong sao kê VETC'],
  month: ['Vé tháng', 'pri', 'Trạm vé tháng · không có trong sao kê'],
  miss: ['Chưa có trong sao kê', 'warn', 'Chưa thấy giao dịch trong sao kê VETC'],
} as const;

const isRed = (x: OpsLine): boolean => x.own === 'H' && x.nat === 'OBH' || (x.nat === 'OBH' && x.doc === 'NDC');
const cls = (...a: (string | false | undefined)[]) => a.filter(Boolean).join(' ');

interface Props { trip: OpsTrip | null; mask: boolean; onClose: () => void }

export function OpsTripDrawer({ trip: t, mask, onClose }: Props) {
  const toast = useToast();
  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });
  const fk = (n: number) => (mask ? MASK : th(n));
  const ff = (n: number) => (mask ? '••••••' : fullVnd(n));
  const finds = t
    ? [
        ...t.lines.filter(isOver).map((x) => ({ k: `${x.name} · vượt định mức ${pct((x.amt - x.norm!) / x.norm!)}`, dot: 'var(--d-amber)', tip: `${ff(x.amt)} / ${ff(x.norm!)}` })),
        ...t.lines.filter(isRed).map((x) => ({ k: `${x.name} · chi hộ, hoá đơn tên HANA`, dot: 'var(--err)', tip: 'Chuyển BIL hoặc yêu cầu xuất lại tên khách' })),
        ...t.lines.filter((x) => x.ph === 0).map((x) => ({ k: `${x.name} · thiếu ảnh chứng từ`, dot: 'var(--d-amber)', tip: 'Nhắc bổ sung ảnh' })),
        ...(t.vMiss ? [{ k: `${t.vMiss} lượt VETC chưa có trong sao kê`, dot: 'var(--d-amber)', tip: 'Đối chiếu lại khi nhập sao kê mới' }] : []),
      ]
    : [];
  const vOk = t ? t.vetc.filter((v) => v.match === 'ok').length : 0;
  const last = t ? t.stopList.length - 1 : 0;
  return (
    <Drawer
      open={!!t}
      onOpenChange={(o) => !o && onClose()}
      title={t?.id ?? ''}
      description={t ? `${STATUS_LABEL[t.status]} · ${PERSON[t.ppl] ?? t.ppl} · ${t.veh} · ${t.from} → ${t.to}` : undefined}
      closeLabel="Đóng"
      footer={t && (
        <div className={s.foot}>
          <b title={`Tổng chi · ${t.lines.length} khoản`}>Tổng chi {ff(t.tot)}</b>
          {t.status === 'apv' && <Button variant="secondary" onClick={noWrite}>{tr('opst.1')}</Button>}
          {t.status === 'apv' && <Button onClick={noWrite}>{tr('opst.2')}</Button>}
          {t.status === 'pnd' && <Button onClick={noWrite}>{tr('opst.3')}</Button>}
        </div>
      )}
    >
      {t && (
        <div className={s.dw}>
          <div className={s.stats}>
            {([[t.job, 'Job'], [t.cont, `Cont · ${CARGO_LABEL[t.cg]}`], [`${String(t.km).replace('.', ',')} km`, 'Km chuyến'], [t.tier, 'Tier lương chặng theo km'], [dayLabel(t.day), 'Ngày chạy']] as const).map(([v, tip]) => (
              <span key={tip} className={s.stat} title={tip} aria-label={tip}>{v}</span>
            ))}
          </div>

          <section aria-label="Kiểm bất thường" className={cls(s.sec, s.ask)}>
            <div className={s.askH}><IconSparkles size={16} aria-hidden="true" />{finds.length ? `Hana thấy ${finds.length} điểm bất thường` : 'Không thấy bất thường'}</div>
            {finds.map((g) => (
              <div key={g.k} className={s.find}><span className={s.dot} style={{ background: g.dot }} aria-hidden="true" /><span title={g.tip}>{g.k}</span></div>
            ))}
          </section>

          <section aria-label="Lộ trình" className={s.sec}>
            <h3 className={s.h3}><IconRoute size={16} aria-hidden="true" />{tr('opst.4')}<em title="Km chuyến · số điểm">{String(t.km).replace('.', ',')} km · {t.stopList.length} điểm</em></h3>
            <div className={s.route}>
              {t.stopList.map((p, i) => (
                <div key={`${p.name}${i}`} className={s.stop}>
                  <span className={s.num} style={{ background: i === 0 || i === last ? 'var(--d-gray)' : 'var(--ok-dot)' }} aria-hidden="true">{i + 1}</span>
                  <b title={p.name}>{p.name}</b>
                  {t.extra > 0 && i > 1 && i < last && <span className={cls(s.pill, s.warn)} title="Điểm chen · cộng lương chặng">{tr('opst.5')}</span>}
                  <span className={s.role}>{p.role}</span>
                  <span className={s.at} title={`${p.role} · ${p.at}`}>{p.at}</span>
                </div>
              ))}
            </div>
          </section>

          <section aria-label="Các khoản chi" className={s.sec}>
            <h3 className={s.h3}><IconReceipt size={16} aria-hidden="true" />{tr('opst.6')}<em title={`Tổng chi · ${t.lines.length} khoản`}>{fk(t.tot)}</em></h3>
            {t.lines.map((x) => {
              const ov = isOver(x);
              const red = isRed(x);
              return (
                <div key={x.key} className={s.ln}>
                  <div className={s.l1}>
                    <span className={s.code} title={FEE_NAME[x.code] ?? x.code}>{x.code}</span>
                    <span className={s.nm} title={`${FEE_NAME[x.code] ?? ''} · ${x.name}`}>{x.name}</span>
                    {ov && <span className={s.mut} style={{ display: 'flex', color: 'var(--warn)' }} title={`Vượt định mức ${pct((x.amt - x.norm!) / x.norm!)}`}><IconAlertTriangle size={14} aria-hidden="true" /></span>}
                    <b className={cls(s.amt, ov && s.warn)} title={x.name}>{fk(x.amt)}</b>
                  </div>
                  <div className={s.l2}>
                    <span className={s.tags} role="group" aria-label={`${NATURE_LABEL[x.nat]} · ${PAYER_LABEL[x.pay]} · ${DOC_LABEL[x.doc]}`}>
                      <span title={NATURE_LABEL[x.nat]}>{x.nat}</span><i aria-hidden="true" />
                      <span title={PAYER_LABEL[x.pay]}>{x.pay}</span><i aria-hidden="true" />
                      <span title={DOC_LABEL[x.doc]}>{x.doc}</span>
                    </span>
                    {x.rc && <span className={s.pill} title="Loại người nhận">{x.rc === 'ind' ? 'Cá nhân' : 'Doanh nghiệp tư nhân'}</span>}
                    {x.norm != null && !x.wage && x.cat !== 'toll' && (
                      <span className={cls(s.pill, ov && s.warn)} title={`Định mức ${ff(x.norm)}${x.nk ? ` · ${x.nk}` : ''}`}>ĐM {fk(x.norm)}{ov ? ` · ${pct((x.amt - x.norm) / x.norm)}` : x.nk ? ` · ${x.nk}` : ''}</span>
                    )}
                    <span className={s.grow} />
                    {x.ph != null && x.ph > 0 && (
                      <button type="button" className={s.ph} title={`Xem ${x.ph} ảnh chứng từ ${x.code}`} aria-label={`Xem ${x.ph} ảnh chứng từ ${x.code}`} onClick={() => toast({ msg: 'Xem ảnh chứng từ chưa mở ở bản này' })}><IconCamera size={14} aria-hidden="true" />{x.ph}</button>
                    )}
                    {x.ph === 0 && <span className={cls(s.ph, s.miss)} role="img" aria-label="Thiếu ảnh chứng từ" title="Thiếu ảnh chứng từ"><IconCamera size={14} aria-hidden="true" />{tr('opst.7')}</span>}
                  </div>
                  {x.wage && (
                    <div role="table" aria-label="Lương chặng theo tier km" className={s.sub}>
                      {x.wage.map(([k, f, v]) => (
                        <div role="row" key={k} className={s.sr}><span>{k}</span><span>{f}</span><span>{fk(v)}</span></div>
                      ))}
                      <div role="row" className={cls(s.sr, s.sum)}><span>{tr('opst.8')}</span><span /><span>{fk(x.amt)}</span></div>
                    </div>
                  )}
                  {red && (
                    <div role="alert" className={s.alert}>
                      <IconAlertTriangle size={14} aria-hidden="true" />{x.own === 'H' ? 'Chi hộ nhưng hoá đơn tên HANA' : 'Chi hộ không có chứng từ'}
                      <span className={s.grow} />
                      <button type="button" onClick={noWrite}>{tr('opst.9')}</button>
                      <button type="button" onClick={noWrite}>{tr('opst.10')}</button>
                    </div>
                  )}
                </div>
              );
            })}
          </section>

          <section aria-label="VETC đối chiếu sao kê" className={s.sec}>
            <h3 className={s.h3}><IconTicket size={16} aria-hidden="true" />{tr('opst.11')}<em title={`${vOk} lượt khớp sao kê · ${t.vMonth} vé tháng · ${t.vMiss} chưa có`} style={{ color: t.vMiss ? 'var(--warn)' : 'var(--ok)' }}>{vOk}/{t.vetc.length} khớp</em></h3>
            <div className={s.box}>
              {t.vetc.map((v, i) => {
                const [label, tone, tip] = MATCH[v.match];
                return (
                  <div key={`${v.name}${i}`} className={s.row}>
                    <span>{v.name}</span>
                    <span className={s.mut} style={{ width: 44 }}>{v.at}</span>
                    <span className={cls(s.pill, tone !== 'ok' ? s[tone] : s.ok)} title={tip}>{label}</span>
                    <b style={{ width: 72, textAlign: 'right', fontWeight: 'var(--fw-label)', color: v.match === 'month' ? 'var(--lb)' : undefined }}>{v.match === 'month' ? '—' : fk(v.amt)}</b>
                  </div>
                );
              })}
            </div>
          </section>

          {!mask && (
            <section aria-label="Phụ thu bán ra từ chuyến" className={s.sec}>
              <h3 className={s.h3}><IconCoin size={16} aria-hidden="true" />Phụ thu bán ra từ chuyến{t.sell.length > 0 && <em>{th(t.sell.reduce((a, y) => a + y.amt, 0))}</em>}</h3>
              {!t.sell.length && <div className={s.empty}>{tr('opst.12')}</div>}
              {t.sell.map((y) => (
                <div key={y.code} className={s.sell}>
                  <span className={s.code} title={FEE_NAME[y.code] ?? y.code}>{y.code}</span>
                  <div><b>{y.name}</b><span title={y.formula}>{y.formula}</span></div>
                  <b>{th(y.amt)}</b>
                  <button type="button" className={s.sellBtn} onClick={noWrite}>{tr('opst.13')}</button>
                </div>
              ))}
            </section>
          )}

          <section aria-label="Lịch sử" className={s.sec}>
            <h3 className={s.h3}><IconHistory size={16} aria-hidden="true" />{tr('opst.14')}</h3>
            <div className={s.box}>
              {history(t).map(([u, a, at]) => (
                <div key={a} className={s.row}>
                  <span className={s.av} aria-hidden="true">{u === 'SYS' ? 'HT' : (PERSON[u] ?? u).slice(0, 2)}</span>
                  <div>{u === 'SYS' ? 'Hệ thống' : (PERSON[u] ?? u)}<small>{a}</small></div>
                  <span className={s.mut}><IconClock size={12} aria-hidden="true" /> {at}</span>
                </div>
              ))}
            </div>
          </section>
          <a className={s.mut} href="/ezy/tms" title={`Mở lệnh vận tải ${t.id}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none' }}><IconExternalLink size={14} aria-hidden="true" />{tr('opst.15')}</a>
        </div>
      )}
    </Drawer>
  );
}

function history(t: OpsTrip): [string, string, string][] {
  const d = dayLabel(t.day);
  const h: [string, string, string][] = [
    [t.ppl, `Gửi ${t.lines.filter((x) => x.ph).length} ảnh chứng từ`, `${d} 17:40`],
    ['SYS', `Ghép VETC theo GPS · ${t.vetc.length} lượt`, `${d} 17:42`],
  ];
  if (t.status === 'ok' || t.status === 'set') h.push(['THANH', 'Duyệt', `${dayLabel(Math.min(22, t.day + 1))} 09:15`]);
  if (t.status === 'set') h.push(['HANH', 'Quyết toán tạm ứng', '22/Oct 10:30']);
  return h;
}
